import {
  Box,
  FlexBox,
  Icon,
  IconButton,
  IconNames,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import cx from 'classnames';
import { useSettingsQuery } from 'lib-knowledge-management-common/graphql';
import { useModal } from 'lib-knowledge-modal';
import { routeGenerator } from 'lib-knowledge-utils/routes';
import { useRef } from 'react';
import { NavLink } from 'react-router-dom';

import { NodeLockIconWithTooltip } from 'components/icons/nodeLockIcon/nodeLockIcon';
import { useInfiniteScroll } from 'hooks/useInfiniteScroll';
import { useShowArticleSuggestions } from 'hooks/useShowArticleSuggestions';

import layoutStyles from '../layout/layout.module.scss';
import { CollectionAddModal } from '../modals/collectionAddModal';

import sidebarStyles from './sidebar.module.scss';

type CollectionLink = {
  title: string;
  id: string;
  internalKbEnabled: boolean;
  isSyncedWithElevio: boolean;
  onClick: (id: string) => void;
};
type SidebarProps = {
  collectionLinks: CollectionLink[];
  hasMore: boolean;
  fetchMore: () => void;
};
export function CollectionSidebar({
  collectionLinks,
  hasMore,
  fetchMore,
}: SidebarProps): JSX.Element {
  const { showModal } = useModal();
  const containerRef = useRef<HTMLDivElement>(null);
  const lastRowRef = useRef<HTMLDivElement>(null);
  useInfiniteScroll({
    containerRef,
    lastRowRef,
    fetchMore,
    hasMore,
    rowCount: collectionLinks.length,
  });
  const { data } = useSettingsQuery();
  function onClickNewCollection() {
    if (data) {
      showModal(CollectionAddModal, {});
    }
  }

  return (
    <div className={sidebarStyles.sidebar} ref={containerRef}>
      <Box className={sidebarStyles.sidebarContent}>
        <FlexBox flexDirection="column" gap="8px" margin="0px 0px 10px 0px">
          <NavLinkItems />
        </FlexBox>
        <FlexBox flexDirection="column" gap="8px">
          <FlexBox justifyContent="space-between" alignItems="center">
            <Text.Heading
              className={layoutStyles.subHeading}
              variant={'heading-4'}
              color="primary"
              as="div"
            >
              Collections
            </Text.Heading>

            <Tooltip content="New Collection">
              <IconButton
                variant="secondary"
                icon="plus"
                onClick={onClickNewCollection}
              />
            </Tooltip>
          </FlexBox>
          {collectionLinks.map((collectionLink: CollectionLink) => {
            return (
              <CollectionItem {...collectionLink} key={collectionLink.id} />
            );
          })}
          <div ref={lastRowRef} />
        </FlexBox>
      </Box>
      <KnowledgeSettingsLink />
    </div>
  );
}

function KnowledgeSettingsLink() {
  return (
    <NavLink
      className={({ isActive }) => {
        return cx(sidebarStyles.sidebarLink, {
          [sidebarStyles.sidebarLinkActive]: isActive,
        });
      }}
      to={routeGenerator.knowledgeSettingsPath()}
    >
      <FlexBox
        gap="300"
        justifyContent="flex-start"
        alignItems="center"
        padding="200 300"
        borderRadius="100"
        className={sidebarStyles.settingsLink}
      >
        <Icon name="cog" />
        <Text.Paragraph variant="3">Settings</Text.Paragraph>
      </FlexBox>
    </NavLink>
  );
}

function NavLinkItems() {
  const isArticlesPage = window.location.pathname === '/knowledge/articles';
  const hasMoreThan1PublicationStatus =
    window.location.search.split('publicationStatus').length - 1 > 1;
  const isNeverPublished =
    isArticlesPage &&
    window.location.search.includes('publicationStatus=NeverPublished') &&
    !hasMoreThan1PublicationStatus;
  const isPublishedWithDraft =
    isArticlesPage &&
    window.location.search.includes('publicationStatus=PublishedWithDraft') &&
    !hasMoreThan1PublicationStatus;
  const viewArticlesPath = `${routeGenerator.articles('')}`;
  const articleSuggestionsPath = routeGenerator.articleSuggestions();
  const showArticleSuggestions = useShowArticleSuggestions();

  return (
    <>
      <NavLinkItem
        to={viewArticlesPath}
        label="All articles"
        iconName="file-text"
        isActive={isArticlesPage && !(isNeverPublished || isPublishedWithDraft)}
      />
      <NavLinkItem
        to={`${viewArticlesPath}?publicationStatus=NeverPublished`}
        label="Pre-release"
        iconName="file-lock"
        isActive={isNeverPublished}
      />
      <NavLinkItem
        to={`${viewArticlesPath}?publicationStatus=PublishedWithDraft`}
        label="Work in progress"
        iconName="file-draft"
        isActive={isPublishedWithDraft}
      />
      {showArticleSuggestions && (
        <NavLinkItem
          to={articleSuggestionsPath}
          label="Article suggestions"
          iconName="ai"
          // Unlike the entries above, this is its own path rather than the
          // articles path with different query params.
          isActive={window.location.pathname === articleSuggestionsPath}
        />
      )}
    </>
  );
}

type IconNameProps = {
  iconName: IconNames;
};

type IconProps = IconNameProps;

type NavLinkItemProps = {
  to: string;
  label: string;
  isActive: boolean;
} & IconProps;
export function NavLinkItem(props: NavLinkItemProps): JSX.Element {
  const { to, label, iconName, isActive } = props;
  return (
    <NavLink
      to={to}
      className={() =>
        cx(sidebarStyles.articlesLink, {
          [sidebarStyles.articlesLinkActive]: isActive,
        })
      }
    >
      <FlexBox alignItems="center" justifyContent="space-between">
        <FlexBox flexDirection="row" alignItems="center" gap="8px">
          <Icon className={sidebarStyles.linkIcon} name={iconName} />
          <Text.Paragraph variant="3">{label}</Text.Paragraph>
        </FlexBox>
      </FlexBox>
    </NavLink>
  );
}

export function CollectionItem({
  id,
  title,
  internalKbEnabled,
  isSyncedWithElevio,
}: CollectionLink): JSX.Element {
  return (
    <NavLink
      to={`/knowledge/collection/${id}`}
      className={({ isActive }) =>
        cx(sidebarStyles.collectionLink, {
          [sidebarStyles.collectionLinkActive]: isActive,
        })
      }
    >
      <FlexBox
        className={sidebarStyles.collectionListItem}
        alignItems="center"
        justifyContent="space-between"
      >
        <FlexBox flexDirection="row" alignItems="center" gap="16px">
          <Icon className={sidebarStyles.linkIcon} name="copy" />
          <Text.Paragraph variant="3">{title}</Text.Paragraph>
        </FlexBox>
        <FlexBox alignItems="center" gap="200">
          {isSyncedWithElevio && (
            <NodeLockIconWithTooltip label="Synced from Elevio" />
          )}
          {!internalKbEnabled && (
            <Tooltip content="Not visible in Internal KB">
              <Icon
                className={sidebarStyles.notVisibleIcon}
                name="eye-slash"
                size={16}
              />
            </Tooltip>
          )}
        </FlexBox>
      </FlexBox>
    </NavLink>
  );
}
